<template>
  <div :class="$style.field">
    <label :for="id">
      {{ prospectLabel }}
      <i v-if="required">*</i>
    </label>
    <multiselect
      :id="id"
      v-model="value"
      label="name"
      track-by="crmId"
      placeholder="Search..."
      :allow-empty="!required"
      :show-labels="!required"
      :internal-search="false"
      :loading="isBusy"
      :show-no-results="true"
      :options="prospects"
      :option-height="54"
      select-label=""
      selected-label="✕"
      deselect-label="✕"
      :custom-label="customLabel"
      ref="select"
    >
      <template #noResult>
        <span>No matches found</span>
      </template>
      <template #noOptions>
        <span>No matches found</span>
      </template>
      <template #option="props">
        <div class="primary">{{ props.option.name }}</div>
        <div
          class="secondary"
          v-if="props.option.type === 'account' && props.option.industry"
        >
          {{ props.option.industry }}
        </div>
        <div
          class="secondary"
          v-if="props.option.organization || props.option.title"
        >
          <template v-if="props.option.title">
            {{ props.option.title
            }}<template v-if="props.option.organization"> at </template>
          </template>
          {{ props.option.organization }}
        </div>
      </template>
    </multiselect>
    <p v-show="error && showError">{{ error }}</p>
  </div>
</template>

<script>
import axios, { CancelToken } from "axios";
import Multiselect from "vue-components/vue-multiselect";
import { createNamespacedHelpers } from "vuex";
import { errorNotification } from "../notifications";

const { mapState, mapMutations } = createNamespacedHelpers("activitySummary");
let cancellationSource;

const NON_INPUT_KEYS = [
  "Enter",
  "ArrowUp",
  "ArrowRight",
  "ArrowDown",
  "ArrowLeft",
  "Escape",
  "Meta",
  "Shift",
  "Control",
  "Tab",
];

export default {
  components: { Multiselect },
  data() {
    return {
      id: "prospect_id",
      value: null,
      isBusy: false,
      prospects: [],
      scopes: ["account", "lead", "contact"],
      showError: false,
      crmName: this.$_activitySummary_config.getTeam().crm.name,
      isLoading: false,
    };
  },
  computed: {
    ...mapState(["activity", "formErrors", "selectedProspect"]),
    error() {
      return this.formErrors[this.id];
    },
    prospectLabel() {
      if (this.crmName === "Salesforce") {
        return "Lead, Contact or Account";
      } else if (this.crmName === "HubSpot") {
        return "Contact or Company";
      } else if (this.crmName === "Pipedrive") {
        return "Person or Organization";
      } else if (this.crmName === "Copper") {
        return "Lead, Person or Company";
      } else if (this.crmName === "Close") {
        return "Contact or Lead";
      } else if (this.crmName === "Bullhorn") {
        return "Candidate or DM";
      }
    },
    required() {
      return !this.activity.isInternal;
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(newOption, oldOption) {
        if (newOption) {
          // Fallback to previous value if new old is empty
          if (newOption.crmId === "") {
            this.value = oldOption;

            return;
          }
        }

        this.updateForm(newOption);
      },
    },
    selectedProspect(prospect) {
      // Selected prospect could be set externally via activity.prospect-updated event
      if (prospect) {
        this.value = prospect;
        if (!this.prospects.find((p) => p.crmId === prospect.crmId)) {
          this.prospects.push(prospect);
        }
      }
    },
  },
  created() {
    this.$_activitySummary_config.EventBus.$on(
      "ACTIVITY_FORM_SUBMITTED",
      () => {
        this.showError = true;
      }
    );

    this.$_activitySummary_config.EventBus.$on("UPDATED_INTERNAL_INPUT", () => {
      this.updateForm(this.value);
    });

    this.value = this.activity.prospect ? this.activity.prospect : null;
  },
  mounted() {
    if (this.crmName === "HubSpot") {
      this.registerHubspotSearchEvent();
    } else {
      this.registerNormalSearchEvent();
    }
  },
  methods: {
    ...mapMutations(["upsertFormData", "setSelectedProspect"]),
    registerHubspotSearchEvent() {
      this.$refs.select.$el.addEventListener("keydown", (event) => {
        if (!NON_INPUT_KEYS.includes(event.key)) {
          // Force reset prospects to show hint on type
          this.prospects = [
            {
              crmId: "",
              name: "Please hit Enter key to search",
            },
          ];
        }

        if (event.key === "Enter") {
          // Enter
          this.prospects = [];
          this.search(this.$refs.select.search);
        }
      });
    },
    registerNormalSearchEvent() {
      // Use "input" event instead of "keydown" to capture backspace key
      this.$refs.select.$el.addEventListener("input", (event) => {
        this.search(this.$refs.select.search);
      });
    },
    updateForm(option) {
      this.showError = false;

      const prospectId = option ? option.crmId : null;
      const prospectType = option ? option.type : null;

      this.upsertFormData({
        id: this.id,
        value: prospectId,
        error: this.validate(prospectId),
      });

      this.upsertFormData({
        id: "prospect_type",
        value: prospectType,
      });
      // If selected prospect is null and a value is picked from the dropdown
      // or the selected value is different from the new value.
      if (
        !this.selectedProspect ||
        this.selectedProspect.crmId !== prospectId
      ) {
        this.setSelectedProspect(option);
      }
    },
    search(query) {
      if (query.length < 2) {
        return;
      }

      this.isBusy = true;

      if (cancellationSource) {
        cancellationSource.cancel();
      }
      cancellationSource = CancelToken.source();

      axios
        .get(
          `${this.$_activitySummary_config.jiminnyHost()}/api/v1/crm/search`,
          {
            cancelToken: cancellationSource.token,
            params: {
              name: query,
              scopes: this.scopes,
            },
          }
        )
        .then((response) => {
          this.prospects = response.data.map((p) => ({
            ...p,
            type: p.prospectType,
          }));

          this.isBusy = false;
        })
        .catch((error) => {
          if (axios.isCancel(error)) {
            return;
          }

          console.error(error);
          errorNotification({
            message: error.response.data.error.message,
          }).show();

          this.isBusy = false;
        });
    },
    validate(value) {
      // If Internal Call is toggled, it should not prompt to complete any fields when you end the meeting.
      return this.required && !value ? "Please select the prospect" : null;
    },
    customLabel(option) {
      return option.name;
    },
  },
};
</script>

<style module lang="less" src="../custom-section/fields/field.less"></style>



// WEBPACK FOOTER //
// select-prospect.vue?71df0cd2